/**
 * Sprint 26.0 — task detail data + mutations (clean build).
 *
 * One hook module backing both pane and page mounts of <TaskDetail>. Reads: the
 * composite detail (get_task → TaskDetailResult), references, sub-tasks, scoped
 * history. Mutations: field edits (body / title / instructions / due_by /
 * status), references add/remove, lifecycle (move to top / triage / drop /
 * complete), and create. Every mutation invalidates the agent brain snapshot so
 * the cockpit behind the pane updates live.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { RecentEvent, Task, TaskDetailResult, TaskReference, TaskReferenceKind } from "../../lib/types";

const POLL_MS = 30_000;
const STALE_MS = 10_000;

/** Invalidate everything the snapshot + pane derive from a task mutation. */
function invalidateTaskWorld(qc: QueryClient, taskId?: string) {
  qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
  qc.invalidateQueries({ queryKey: ["agent-tasks"] });
  qc.invalidateQueries({ queryKey: ["agent-queue"] });
  qc.invalidateQueries({ queryKey: ["agent-inbox"] });
  qc.invalidateQueries({ queryKey: ["needs-you"] });
  if (taskId) {
    qc.invalidateQueries({ queryKey: ["task-detail", taskId] });
    qc.invalidateQueries({ queryKey: ["task-references", taskId] });
    qc.invalidateQueries({ queryKey: ["task-history", taskId] });
  }
}

export function useTaskDetail(taskId: string | undefined, enabled = true) {
  return useQuery<TaskDetailResult>({
    queryKey: ["task-detail", taskId],
    queryFn: () => mcpClient.getTask(taskId as string),
    enabled: enabled && !!taskId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
  });
}

export function useTaskReferences(taskId: string | undefined, enabled = true) {
  return useQuery<TaskReference[]>({
    queryKey: ["task-references", taskId],
    queryFn: () => mcpClient.listTaskReferences(taskId as string),
    enabled: enabled && !!taskId,
    staleTime: STALE_MS,
  });
}

export function useTaskHistory(taskId: string | undefined, enabled = true) {
  return useQuery<RecentEvent[]>({
    queryKey: ["task-history", taskId],
    queryFn: () => mcpClient.listTaskHistory(taskId as string),
    enabled: enabled && !!taskId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
  });
}

/** Patch a single task field (title / body / instructions / due_by / status / parent_task_id). */
export function useUpdateTaskField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, taskId, fields }: { agentId: string; taskId: string; fields: Record<string, unknown> }) =>
      mcpClient.updateTask(agentId, taskId, fields),
    onSuccess: (_t, vars) => invalidateTaskWorld(qc, vars.taskId),
  });
}

export function useAddTaskReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, kind, targetId }: { taskId: string; kind: TaskReferenceKind; targetId: string }) =>
      mcpClient.addTaskReference(taskId, kind, targetId),
    onSuccess: (refs, vars) => {
      qc.setQueryData(["task-references", vars.taskId], refs);
      invalidateTaskWorld(qc, vars.taskId);
    },
  });
}

export function useRemoveTaskReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, kind, targetId }: { taskId: string; kind: TaskReferenceKind; targetId: string }) =>
      mcpClient.removeTaskReference(taskId, kind, targetId),
    onSuccess: (refs, vars) => {
      qc.setQueryData(["task-references", vars.taskId], refs);
      invalidateTaskWorld(qc, vars.taskId);
    },
  });
}

/** "Make current" — move this task to rank #1 (lowest sort_order). */
export function useMoveTaskToTop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => mcpClient.moveTaskToTop(taskId),
    onSuccess: (_t, vars) => invalidateTaskWorld(qc, vars.taskId),
  });
}

/** Triage an inbox task into the queue. */
export function useTriageTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId }: { taskId: string }) => mcpClient.triageTask(taskId),
    onSuccess: (_t, vars) => invalidateTaskWorld(qc, vars.taskId),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, taskId, summary }: { agentId: string; taskId: string; summary?: string }) =>
      mcpClient.completeTask(agentId, taskId, summary),
    onSuccess: (_t, vars) => invalidateTaskWorld(qc, vars.taskId),
  });
}

export function useDropTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) => mcpClient.dropTask(taskId, reason),
    onSuccess: (_t, vars) => invalidateTaskWorld(qc, vars.taskId),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      agent_id: string;
      title: string;
      body?: string;
      instructions?: string;
      due_by?: string;
      parent_task_id?: string;
      status?: "inbox" | "queued";
      workflow_instance_id?: string;
    }): Promise<{ task: Task; warning: string | null }> => mcpClient.createTask(params),
    onSuccess: () => invalidateTaskWorld(qc),
  });
}
